import { useState as classicUseState, useContext } from 'react';

import ReactDom from 'react-dom';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Controller, useForm } from 'react-hook-form';

import { State, useState } from '@hookstate/core';
import { yupResolver } from '@hookform/resolvers/yup';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';

import { updateEquipmentStatus } from '@services/queries/equipmentStatus/updateEquipmentStatus/updateEquipmentStatus';

import EquipmentStatus, {
  EquipmentStatusCategoryOptions,
} from '@models/EquipmentStatus.model';

import { NavigationContext } from '@layout/navigation/navigation';
import { DeleteDialog, Toggle } from '@components/index';
import { showDefaultStatus } from '@services/queries/studios/showDefaultStatus/showDefaultStatus';

import { setAttr } from '@utils/setAttr';
import { nullIfEmpty } from '@utils/nullIfEmpty';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Label,
  Line,
  Input,
  Form,
  Divider,
  Textarea,
  Error,
  LabelOptional,
  YesNo,
  RowDiv,
} from './EditEquipmentStatus.styles';
import { formSchema, typeOptions } from '../../CustomStatus.utils';
import { EditEquipmentStatusLoading } from './EditEquipmentStatus.loading';

interface IEditEquipmentStatus {
  open: State<boolean>;
  equipmentStatus: EquipmentStatus;
}

interface IFormData {
  name: string;
  description: string | null;
  type: EquipmentStatusCategoryOptions;
  isDefault: boolean;
}

export const EditEquipmentStatus = ({
  open,
  equipmentStatus,
}: IEditEquipmentStatus) => {
  const portalDiv = document.getElementById('portal');

  const { studioId } = useContext(NavigationContext);

  const openDeleteDialog = useState<boolean>(false);

  const queryClient = useQueryClient();

  const defaultStatus = useQuery(['defaultStatus', studioId], () =>
    showDefaultStatus({ id: studioId }),
  );

  const [defaultStatusState, setDefaultStatusState] = classicUseState<boolean>(
    defaultStatus.data === equipmentStatus.id,
  );

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    control,
    setError,
    reset,
    clearErrors,
  } = useForm<IFormData>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: equipmentStatus.name,
      description: equipmentStatus.description,
      type: equipmentStatus.category,
      isDefault: equipmentStatus.id === defaultStatus.data,
    },
    mode: 'onChange',
  });

  const close = () => {
    open.set(false);
    reset();
    clearErrors('name');
  };

  const updateEquipmentStatusMutation = useMutation(updateEquipmentStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studios', studioId, 'customStatus']);
      queryClient.invalidateQueries(['defaultStatus', studioId]);
      close();
    },
    onError: (error: any) => {
      if (
        error.response?.data?.message ===
        'There is already a custom status with this name'
      ) {
        setError('name', {
          type: 'validate',
          message: 'There is already a custom status with this name',
        });
      }
    },
  });

  const onSubmit = (data: IFormData) => {
    updateEquipmentStatusMutation.mutate({
      id: equipmentStatus.id,
      studioId,
      name: data.name,
      description: data.description,
      category: data.type,
      isDefault: defaultStatusState,
    });
  };

  if (defaultStatus.isLoading) {
    return <EditEquipmentStatusLoading open={open} />;
  }

  const toggleDefaultStatus = () => {
    if (
      defaultStatus.data &&
      defaultStatus.data !== equipmentStatus.id &&
      !defaultStatusState
    ) {
      openDeleteDialog.set(true);
    }
    return setDefaultStatusState(!defaultStatusState);
  };

  return open.get() && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>{`Editing ${equipmentStatus.name}`}</Title>
              <Close onClick={close}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label>Name</Label>

              <Input
                type="text"
                placeholder="Choose a Custom Status Name"
                {...register('name')}
                onChange={e => {
                  register('name').onChange(e);
                }}
                className="infoInputs"
                isError={
                  errors.name &&
                  errors.name.message ===
                    'There is already a custom status with this name'
                }
              />

              {errors.name &&
                errors.name.message ===
                  'There is already a custom status with this name' && (
                  <Error>{errors.name.message}</Error>
                )}

              <LabelOptional>Description (Optional)</LabelOptional>

              <Textarea
                placeholder="Placeholder"
                {...register('description', nullIfEmpty)}
                onChange={e => {
                  register('description').onChange(e);
                }}
                className="infoInputs"
              />

              <Label>Type</Label>

              <Controller
                control={control}
                name="type"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={typeOptions}
                    value={typeOptions.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                    }}
                    placeholder="Select a field type"
                  />
                )}
              />

              <Label>Default Status</Label>

              <Label className="description">
                If set, this status will be selected when adding assets
              </Label>

              <RowDiv>
                <Toggle
                  selected={defaultStatusState}
                  toggleSelected={() => {
                    toggleDefaultStatus();
                  }}
                />
                <YesNo>{defaultStatusState ? 'Yes' : 'No'}</YesNo>
              </RowDiv>

              <Line>
                <Confirm disabled={setAttr(!isValid)} type="submit">
                  Save
                </Confirm>
              </Line>
            </Form>

            <DeleteDialog
              open={openDeleteDialog}
              onDelete={() => {
                setDefaultStatusState(true);
                openDeleteDialog.set(false);
              }}
              backgroundColor="#FF9900"
              buttonText="Replace Default Status"
              text="There is already one status set as default"
              onCancel={() => setDefaultStatusState(false)}
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
